import useGTM from "@hooks/useGTM";
import { useStepFlow } from "@hooks/useStepFlow";
import { useAuthCreatePurchase } from "@mutations/useAuthCreatePurchase";
import { useAuthFinalizePurchase } from "@mutations/useAuthFinalizePurchase";
import { useCreatePurchase } from "@mutations/useCreatePurchase";
import { useFinalizePurchase } from "@mutations/useFinalizePurchase";
import { useUpdatePurchase } from "@mutations/useUpdatePurchase";
import type { ComponentType, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useToken } from "./TokenContext";

// Step component type - using any because components have different prop interfaces
export type StepComponent = ComponentType<any>;

export interface PurchaseFlowContextValue {
  // From useStepFlow - step configuration
  stepComponents: StepComponent[];
  stepTitles: Record<string, string>;
  authenticatedStepTitles: Record<string, string>;
  stepLabels: string[];
  hasAccessoryStep: boolean;
  hasShippingStep: boolean;
  activeStepIndex: number;

  // Navigation state
  currentStep: number;
  setCurrentStep: (step: number) => void;
  goNext: () => Promise<void>;
  goBack: () => void;

  // Validation state
  hasAttemptedValidation: boolean;
  setHasAttemptedValidation: (value: boolean) => void;

  // Loading state
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  loadingMessage: string;
  setLoadingMessage: (message: string) => void;

  // Environment
  isMockup: boolean;
  isAuthenticated: boolean | undefined;

  // Purchase operations
  handleCreatePurchase: (values: any, uuid: string) => void;
  handleConfirmPurchase: () => Promise<void>;
  updatePurchase: (payload: any) => Promise<any>;
  isUpdatingPurchase: boolean;

  // Step state
  isOnConfirmStep: boolean;
}

const PurchaseFlowContext = createContext<PurchaseFlowContextValue | undefined>(undefined);

export type CartItem = {
  sku: string;
  quantity?: number;
};

export interface PurchaseFlowProviderProps {
  children: ReactNode;
  isMockup?: boolean;
  items?: CartItem[];
  currency?: string;
}

export const PurchaseFlowProvider = ({ children, isMockup = false }: PurchaseFlowProviderProps) => {
  const { isAuthenticated } = useToken();
  const methods = useFormContext();
  const navigate = useNavigate();

  // Get step flow configuration
  const {
    stepComponents,
    stepTitles,
    authenticatedStepTitles,
    stepLabels,
    hasAccessoryStep,
    hasShippingStep,
    activeStepIndex,
    currentStep,
    isOnConfirmStep,
  } = useStepFlow(isAuthenticated);

  // UI state
  const [hasAttemptedValidation, setHasAttemptedValidation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  // Purchase mutations
  const { mutate: createPurchase, isPending: isCreating } = useCreatePurchase();
  const { mutate: authCreatePurchase } = useAuthCreatePurchase({
    onSuccess: (data) => {
      methods.setValue("uuid", data.uuid);
    },
  });
  const { mutateAsync: finalizePurchase, isPending: isFinalizing } = useFinalizePurchase();
  const { mutateAsync: authFinalizePurchase, isPending: isAuthFinalizing } =
    useAuthFinalizePurchase();
  const { mutateAsync: updatePurchase, isPending: isUpdatingPurchase } = useUpdatePurchase();

  const { purchase } = useGTM();

  // Navigation logic
  const setCurrentStep = useCallback(
    (step: number) => {
      methods.setValue("currentStep", step);
    },
    [methods],
  );

  const goNext = useCallback(async () => {
    setHasAttemptedValidation(true);
    const valid = await methods.trigger();

    if (!valid) return;

    // Reset validation state for the new step
    setHasAttemptedValidation(false);
    methods.setValue("currentStep", currentStep + 1);
  }, [currentStep, methods]);

  const goBack = useCallback(() => {
    if (currentStep > 1 && !isAuthenticated) {
      setHasAttemptedValidation(false);
      methods.setValue("currentStep", currentStep - 1);
    } else if (isAuthenticated) {
      if (currentStep > 1) {
        setHasAttemptedValidation(false);
        methods.setValue("currentStep", currentStep - 1);
      } else {
        // Navigate back to dashboard
        window.parent.postMessage({ action: "navigateBack" }, "*");
      }
    }
  }, [currentStep, isAuthenticated, methods]);

  // Purchase operations
  const handleCreatePurchase = useCallback(
    (values: any, uuid: string) => {
      if (!uuid) {
        setLoadingMessage("initializing...");
        createPurchase(
          {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            organizationName: values.organizationName,
            BIData: { source: isMockup ? "mockup" : "store-form" },
            currency: values.selectedCurrency || "usd",
          },
          {
            onSuccess: (res) => {
              methods.setValue("uuid", res.uuid || "");
            },
          },
        );
      }
    },
    [isMockup, createPurchase, methods],
  );

  const handleConfirmPurchase = useCallback(async () => {
    const formValues = methods.getValues();
    const uuid = formValues.uuid;
    const stripePaymentMethodId = formValues.stripePaymentMethodId;

    if (!uuid || !stripePaymentMethodId) {
      return;
    }

    const selectedShipping = formValues.shipmentRate || null;

    // Clear any previous purchase errors
    methods.setValue("purchaseError", "");

    try {
      setLoadingMessage("finalizing purchase...");

      if (isAuthenticated) {
        // Use authenticated finalize purchase
        const authParams = {
          stripe_payment_method_id: stripePaymentMethodId,
        };

        await authFinalizePurchase({ uuid, params: authParams });

        // For authenticated users, navigate with form state context
        navigate(
          `/purchase-complete?firstName=${formValues.firstName}&email=${formValues.email}&currency=${formValues.selectedCurrency || "usd"}`,
          {
            state: {
              purchaseContext: "authenticated",
              formData: formValues,
              timestamp: Date.now(),
            },
          },
        );
      } else {
        // Use regular finalize purchase for non-authenticated users
        await finalizePurchase({ uuid, stripePaymentMethodId });

        // Track purchase completion in GTM
        purchase({
          transaction_id: stripePaymentMethodId,
          shipping: selectedShipping?.otherCost || 0,
          tax: methods.getValues("totalTax"),
        });

        await navigate(
          `/purchase-complete?firstName=${formValues.firstName}&email=${formValues.email}&currency=${formValues.selectedCurrency || "usd"}`,
        );
      }
    } catch (err) {
      console.error(`Error in purchase flow: ${err}`);
      // Store error in form context for Confirm component to display
      const errorMessage =
        (err as Error)?.message ||
        "An error occurred while finalizing your purchase. Please try again.";
      methods.setValue("purchaseError", errorMessage);
    }

    setLoadingMessage("");
  }, [methods, isAuthenticated, authFinalizePurchase, finalizePurchase, purchase, navigate]);

  // Auto-create purchase for authenticated users on mount
  useEffect(() => {
    if (isAuthenticated && !methods.getValues("uuid")) {
      authCreatePurchase();
    }
  }, [isAuthenticated, authCreatePurchase, methods]);

  // Combine loading states
  const combinedIsLoading =
    isLoading || isCreating || isFinalizing || isAuthFinalizing || isUpdatingPurchase;

  const value = useMemo(
    () => ({
      // Step configuration
      stepComponents,
      stepTitles,
      authenticatedStepTitles,
      stepLabels,
      hasAccessoryStep,
      hasShippingStep,
      activeStepIndex,

      // Navigation
      currentStep,
      setCurrentStep,
      goNext,
      goBack,

      // Validation
      hasAttemptedValidation,
      setHasAttemptedValidation,

      // Loading
      isLoading: combinedIsLoading,
      setIsLoading,
      loadingMessage,
      setLoadingMessage,

      // Environment
      isMockup,
      isAuthenticated,

      // Purchase operations
      handleCreatePurchase,
      handleConfirmPurchase,
      updatePurchase,
      isUpdatingPurchase,
      isOnConfirmStep,
    }),
    [
      stepComponents,
      stepTitles,
      authenticatedStepTitles,
      stepLabels,
      hasAccessoryStep,
      hasShippingStep,
      activeStepIndex,
      currentStep,
      setCurrentStep,
      goNext,
      goBack,
      hasAttemptedValidation,
      combinedIsLoading,
      loadingMessage,
      isMockup,
      isAuthenticated,
      handleCreatePurchase,
      handleConfirmPurchase,
      updatePurchase,
      isUpdatingPurchase,
      isOnConfirmStep,
    ],
  );

  return <PurchaseFlowContext.Provider value={value}>{children}</PurchaseFlowContext.Provider>;
};

export const usePurchaseFlow = () => {
  const context = useContext(PurchaseFlowContext);
  if (context === undefined) {
    throw new Error("usePurchaseFlow must be used within a PurchaseFlowProvider");
  }
  return context;
};
